import { IcFilter } from '@assets/icons/24x24';
import { Button, HStack, Icon, Separator, Text, VStack } from '@chakra-ui/react';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { ScrollArea } from '@components/ui/ScrollArea';
import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { NotificationFamilyEnum } from '@/types/data/data.types';

import { defaultAvailableFilterOptionsByCategory } from '../useDisplaySearchFilterOptions';

interface SearchFilterTabProps {
  category: NotificationFamilyEnum;
  onReset?: () => void;
}

export default function SearchFilterTab({
  category,
  onReset,
  children,
}: Readonly<PropsWithChildren<SearchFilterTabProps>>) {
  const { isDirty, resetFilters, openedFilterOptions, setOpenedFilterOptions, displayedFilterOptions } =
    useNotificationSearchContext();
  const canReset = isDirty || !shallowEqual(displayedFilterOptions, defaultAvailableFilterOptionsByCategory[category]);

  const handleReset = () => {
    resetFilters(category);
    if (onReset) {
      onReset();
    }
  };

  return (
    <HStack height="100%" gap={0}>
      <VStack gap={0.25} backgroundColor="neutral.black" height="100%" width="400px" flexShrink={0} userSelect="none">
        <HStack
          width="100%"
          height="56px"
          backgroundColor="neutral.850"
          paddingX={2}
          flexShrink={0}
          justifyContent="space-between"
        >
          <Button
            variant="toggle"
            size="toggleS"
            data-active={canReset}
            disabled={!canReset}
            onClick={() => handleReset()}
          >
            <Text>
              <FormattedMessage id="components.notificationSearch.filters.reset" />
            </Text>
          </Button>
          <Icon
            as={IcFilter}
            color="neutral.300"
            cursor="pointer"
            onClick={() => setOpenedFilterOptions(!openedFilterOptions)}
            _hover={{ color: 'skyBlue' }}
          />
        </HStack>
        <ScrollArea>
          <VStack
            width="100%"
            height="100%"
            backgroundColor="neutral.850"
            gap={0}
            separator={<Separator borderColor="neutral.900" />}
          >
            {children}
          </VStack>
        </ScrollArea>
      </VStack>
    </HStack>
  );
}
